import { CellContext } from '@tanstack/react-table';
import clsx from 'clsx';
import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { PosMultiSelect } from 'src/core/POS/PosMultiSelect';
import { MultiSelectValueChange } from 'src/core/POS/PosMultiSelect/PosMultiSelect.types';
import { vars } from 'src/core/themes';
import * as styles from 'src/tables/SpreadsheetTable/SpreadsheetTable.css';
import { ContentId } from 'src/utils/constants/contentId';
import { newIntId } from 'src/utils/idUtils';
import { posChangedField } from 'src/utils/posFieldUtils';
import { PurchaseTicketsInput } from 'src/utils/purchaseUtils';
import {
  ListingNote,
  ListingNoteType,
  TicketGroupInput,
} from 'src/WebApiController';

function TicketGroupInputSeatTraitComponent({
  cell,
  valueOptionsContent,
  listingNotesMap,
}: CellContext<TicketGroupInput, unknown> & {
  valueOptionsContent: Record<string, ContentId | string>;
  listingNotesMap?: Record<string, ListingNote> | null;
}) {
  const { setValue, watch } = useFormContext<PurchaseTicketsInput>();
  const listingNotes = watch(`ticketGroups.${cell.row.index}.listingNotes`);
  const onSelectionChanged = useCallback(
    ({ newValues: lnIds, searchText }: MultiSelectValueChange<string>) => {
      if (!listingNotesMap) {
        return;
      }
      const newListingNotes = lnIds.map((lnId) => {
        const listingNoteId = parseInt(lnId);
        return listingNotesMap[listingNoteId];
      });

      if (searchText) {
        const newListingNoteId = newIntId();
        const newListingNote = {
          listingNoteId: newListingNoteId,
          listingNoteText: searchText,
          listingNoteUntranslated: searchText,
          sellerAccountId: null, // this gets filled in the backend
          stubhubListingNoteId: null,
          type: ListingNoteType.Information,
        };

        newListingNotes.push(newListingNote);
        listingNotesMap[newListingNoteId] = newListingNote;
        valueOptionsContent[newListingNoteId] = searchText;
      }

      setValue(
        `ticketGroups.${cell.row.index}.listingNotes`,
        posChangedField(newListingNotes)
      );
    },
    [cell.row.index, listingNotesMap, setValue, valueOptionsContent]
  );

  return (
    <div className={styles.listingNotesCellWrapper}>
      <PosMultiSelect
        showNewOption
        values={
          listingNotes?.value?.map((ln) => {
            const listingNote = listingNotesMap?.[ln.listingNoteId];
            if (listingNote) {
              return ln.listingNoteId.toString();
            }

            return ln.listingNoteId
              ? ln.listingNoteId.toString()
              : ln.listingNoteText || ln.listingNoteUntranslated;
          }) ?? []
        }
        allowSearchAsInput
        triggerProps={{
          className: clsx(
            styles.tableCell[
              !listingNotes?.value || listingNotes?.value?.length === 0
                ? 'disabled'
                : 'active'
            ]
          ),
        }}
        style={{
          fontSize: vars.typography.fontSize.sm,
        }}
        searchable
        onChange={onSelectionChanged}
        valueOptionsContent={valueOptionsContent}
      />
    </div>
  );
}

export const TicketGroupInputSeatTrait = React.memo(
  TicketGroupInputSeatTraitComponent
) as (
  props: CellContext<TicketGroupInput, unknown> & {
    valueOptionsContent?: Record<string, ContentId | string>;
    listingNotesMap?: Record<string, ListingNote> | null;
  }
) => JSX.Element;
